// src/utils/errorMessage.js
//
// User-facing error sanitizer.
// Backend job records sometimes contain raw upstream provider errors
// (e.g. "<Provider> API error: 400 Client Error: Bad Request for url:
// https://api.<provider>.io/v1/dubbing"). We should never surface vendor
// names or internal URLs to end users. This helper detects common failure
// categories and returns a friendly, brand-safe message.

// Vendor / infra names we must never leak. Anything matching → treat as
// "unknown vendor error" and fall back to a generic category or default.
const VENDOR_PATTERNS = [
  /eleven\s*labs?/i,
  /open\s*ai/i,
  /anthropic/i,
  /claude/i,
  /gemini/i,
  /google\s*(cloud|api|apis)/i,
  /gcp\b/i,
  /aws\b/i,
  /amazon\s*(web\s*services|s3|polly|transcribe)/i,
  /azure/i,
  /runpod/i,
  /replicate\.com/i,
  /deepgram/i,
  /assembly\s*ai/i,
  /whisper/i,
  /deepl/i,
  /hugging\s*face/i,
  /modal\.com/i,
  /supabase/i,
  /firebase/i,
  /stripe/i,
];

// Vendor URLs we must strip if present.
const VENDOR_URL_RE = /https?:\/\/[^\s"')]+/gi;

// Category → friendly message mapping. First match wins.
const CATEGORY_RULES = [
  {
    match: /\b(429|rate.?limit|too many requests|quota exceed|quota exceeded)\b/i,
    message: "We hit a temporary processing limit. Please retry in a few minutes.",
  },
  {
    match: /\b(timeout|timed out|deadline exceeded)\b/i,
    message: "Processing timed out. Please retry — if it keeps happening, try a shorter clip.",
  },
  {
    match: /\b(network|econnrefused|enotfound|dns|connection reset)\b/i,
    message: "We couldn't reach our processing service. Please retry in a moment.",
  },
  {
    match: /\b(401|403|unauthorized|forbidden|authentication|invalid.*(api.?key|token))\b/i,
    message: "Processing failed due to a service configuration issue. Our team has been notified.",
  },
  {
    match: /\b(400|bad request|invalid.*(input|payload|format|request))\b/i,
    message: "We couldn't process this file. Check the source audio/video and try again.",
  },
  {
    match: /\b(unsupported|not supported|invalid.*(language|format|codec))\b/i,
    message: "This file format or language combination isn't supported yet.",
  },
  {
    match: /\b(500|502|503|504|internal server|bad gateway|service unavailable|gateway timeout)\b/i,
    message: "Our processing service is temporarily unavailable. Please try again shortly.",
  },
  {
    match: /\b(file too large|payload too large|413|max.*(size|duration))\b/i,
    message: "This file exceeds our current size or length limits.",
  },
];

const DEFAULT_MESSAGE =
  "Processing failed. Please retry — if the issue persists, contact support.";

/**
 * Returns a brand-safe, user-friendly error string.
 * - Strips vendor names / internal URLs.
 * - Maps recognizable failure categories to helpful copy.
 * - Falls back to a generic message when nothing matches.
 *
 * @param {unknown} raw
 * @returns {string}
 */
export function sanitizeErrorMessage(raw) {
  if (raw == null) return DEFAULT_MESSAGE;
  const text = String(raw).trim();
  if (!text) return DEFAULT_MESSAGE;

  const containsVendor = VENDOR_PATTERNS.some((re) => re.test(text));
  const containsVendorUrl = VENDOR_URL_RE.test(text);
  // Reset lastIndex since VENDOR_URL_RE is /g.
  VENDOR_URL_RE.lastIndex = 0;

  // Try category classification on the raw text (we want the *reason* even
  // if the vendor name is present — e.g. "<Vendor> 429" → rate-limit copy).
  for (const rule of CATEGORY_RULES) {
    if (rule.match.test(text)) return rule.message;
  }

  // If we couldn't classify AND the message mentions a vendor / URL,
  // hide it completely rather than showing a scrubbed but confusing string.
  if (containsVendor || containsVendorUrl) return DEFAULT_MESSAGE;

  // Otherwise the original text is safe-ish. Trim overly long / stack-y
  // messages to a single readable sentence.
  const oneLine = text.replace(/\s+/g, " ");
  if (oneLine.length > 180) return DEFAULT_MESSAGE;
  return oneLine;
}
